import { useEffect, useState } from "react";
import { Card, Row, Col, Spin, Typography, Space, Statistic } from "antd";
import {
   PhoneOutlined,
   CheckCircleOutlined,
   CloseCircleOutlined,
   ClockCircleOutlined,
   UserOutlined,
   RiseOutlined,
} from "@ant-design/icons";
import {
   XAxis,
   YAxis,
   Tooltip,
   CartesianGrid,
   BarChart,
   Bar,
   ResponsiveContainer,
   Legend,
   Area,
   AreaChart,
} from "recharts";
import { getCallAnalytics } from "../../services/callAnalyticsApi";

const { Title } = Typography;

const CallAnalytics = () => {
   const [data, setData] = useState(null);
   const [loading, setLoading] = useState(false);

   useEffect(() => {
      fetchAnalytics();
   }, []);

   const fetchAnalytics = async () => {
      try {
         setLoading(true);
         const res = await getCallAnalytics();
         setData(res.data);
      } catch (err) {
         console.error(err);
      } finally {
         setLoading(false);
      }
   };

   if (loading || !data) {
      return (
         <div
            style={{
               display: "flex",
               justifyContent: "center",
               alignItems: "center",
               height: "100vh",
            }}
         >
            <Spin size="large" tip="Loading Analytics..." />
         </div>
      );
   }

   // Calculate answer rate
   const stats = data?.stats || {};

   const total = stats.total_calls || 0;
   const answered = stats.answered_calls || 0;

   const answerRate = total
      ? ((answered / total) * 100).toFixed(1)
      : 0;

   return (
      <div style={{ padding: 24, backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
         {/* Header */}
         <Card style={{ marginBottom: 24 }}>
            <Title level={3} style={{ margin: 0 }}>
               <RiseOutlined style={{ marginRight: 10, color: "#1890ff" }} />
               Call Analytics Dashboard
            </Title>
         </Card>

         {/* Stats Cards */}
         <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
               <Card hoverable>
                  <Statistic
                     title="Total Calls"
                     value={stats.total_calls || 0}
                     prefix={<PhoneOutlined />}
                     valueStyle={{ color: "#1890ff", fontSize: 32 }}
                  />
               </Card>
            </Col>

            <Col xs={24} sm={12} md={6}>
               <Card hoverable>
                  <Statistic
                     title="Answered Calls"
                     value={stats.answered_calls || 0}
                     prefix={<CheckCircleOutlined />}
                     valueStyle={{ color: "#52c41a", fontSize: 32 }}
                  />
               </Card>
            </Col>

            <Col xs={24} sm={12} md={6}>
               <Card hoverable>
                  <Statistic
                     title="Missed Calls"
                     value={stats.missed_calls || 0}
                     prefix={<CloseCircleOutlined />}
                     valueStyle={{ color: "#ff4d4f", fontSize: 32 }}
                  />
               </Card>
            </Col>

            <Col xs={24} sm={12} md={6}>
               <Card hoverable>
                  <Statistic
                     title="Answer Rate"
                     value={answerRate}
                     suffix="%"
                     prefix={<ClockCircleOutlined />}
                     valueStyle={{
                        color: answerRate >= 80 ? "#52c41a" : "#faad14",
                        fontSize: 32,
                     }}
                  />
               </Card>
            </Col>
         </Row>

         {/* Charts Row */}
         <Row gutter={[16, 16]}>
            {/* Daily Calls Trend */}
            <Col xs={24} lg={12}>
               <Card
                  title={
                     <Space>
                        <RiseOutlined style={{ color: "#1890ff" }} />
                        <span>Daily Call Trend</span>
                     </Space>
                  }
               >
                  <ResponsiveContainer width="100%" height={300}>
                     <AreaChart data={data.trend}>
                        <defs>
                           <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#1890ff" stopOpacity={0.8} />
                              <stop offset="95%" stopColor="#1890ff" stopOpacity={0.1} />
                           </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                           dataKey="date"
                           tick={{ fontSize: 12 }}
                           tickFormatter={(date) =>
                              new Date(date).toLocaleDateString("en-US", {
                                 month: "short",
                                 day: "numeric",
                              })
                           }
                        />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip
                           contentStyle={{
                              borderRadius: 8,
                              border: "none",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                           }}
                           labelFormatter={(date) =>
                              new Date(date).toLocaleDateString()
                           }
                        />
                        <Legend />
                        <Area
                           type="monotone"
                           dataKey="total"
                           stroke="#1890ff"
                           strokeWidth={2}
                           fill="url(#colorTotal)"
                           name="Total Calls"
                        />
                     </AreaChart>
                  </ResponsiveContainer>
               </Card>
            </Col>

            {/* Agent Performance */}
            <Col xs={24} lg={12}>
               <Card
                  title={
                     <Space>
                        <UserOutlined style={{ color: "#52c41a" }} />
                        <span>Agent Performance</span>
                     </Space>
                  }
               >
                  <ResponsiveContainer width="100%" height={300}>
                     <BarChart data={data.agents} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 12 }} />
                        <YAxis
                           type="category"
                           dataKey="agent_name"
                           tick={{ fontSize: 12 }}
                           width={80}
                        />
                        <Tooltip
                           contentStyle={{
                              borderRadius: 8,
                              border: "none",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                           }}
                        />
                        <Legend />
                        <Bar
                           dataKey="total_calls"
                           fill="#52c41a"
                           name="Total Calls"
                           radius={[0, 4, 4, 0]}
                        />
                     </BarChart>
                  </ResponsiveContainer>
               </Card>
            </Col>
         </Row>
      </div>
   );
};

export default CallAnalytics;